import React, { useMemo } from "react";
import { useNavigate } from "react-router";

import Layout from "./Layout";
import AdminName from "./StepsComponents/AdminName";
import ApplicationName from "./StepsComponents/ApplicationName";
import SelectRole from "./StepsComponents/SelectRole";
import Dashboard from "./StepsComponents/Dashboard";
import RequestOverview from "./StepsComponents/RequestOverview";
import useApplicationForm from "../../hooks/useApplicationForm";
import { ApiResponseMessages } from "../Schemas/constants";
import useStore, { setUserSelector } from "../../store";
import useRequestCreateNewApplication from "../../hooks/useRequestCreateNewApplication";
import useGetLoggedInUser from "../../hooks/useGetLoggedInUser";
import { ROUTE_NEW_USER } from "../../utils/constants";

export interface CreateNewApplicationSteps {
	index: number;
	id: string;
	label: string;
	Element?: React.ComponentType<any>;
	skipable: boolean;
	isValid: boolean;
}

const CreateNewApplicationForm = () => {
	const [activeStep, setActiveStep] = React.useState<number>(0);
	const navigate = useNavigate();
	const [toast, setToast] = React.useState<string>("");
	const { value, valid, validation, handleChange, shouldValidate } = useApplicationForm();

	const { requestCreateApplication, loading: loadingCreateNewApp } = useRequestCreateNewApplication();
	const { getUser, loading: userLoading, data: userData } = useGetLoggedInUser();

	const setUser = useStore(setUserSelector);

	const onCreateApplication = React.useCallback(async () => {
		if (value.hasReadDocumentation) {
			const createApplicationResponse = await requestCreateApplication(value);
			if (createApplicationResponse.toLowerCase() === ApiResponseMessages.Success.toLowerCase()) {
				setToast("Successfully requested new application");
				await getUser();
				navigate(ROUTE_NEW_USER);
			} else {
				setToast(createApplicationResponse || "Something went wrong while creating application.");
			}
		}
	}, [getUser, navigate, requestCreateApplication, value]);

	React.useEffect(() => {
		if (userData) {
			setUser(userData);
		}
	}, [userData, setUser]);

	const CREATE_NEW_APPLICATION_STEPS: CreateNewApplicationSteps[] = useMemo(
		() => [
			{
				index: 0,
				id: "step1",
				label: "Admin Name",
				Element: AdminName,
				skipable: false,
				isValid: !!valid.hasReadDocumentation && value.email !== "",
			},
			{
				index: 1,
				id: "step2",
				label: "Application name and ID",
				Element: ApplicationName,
				skipable: false,
				isValid: valid.appName && valid.appId,
			},
			{
				index: 2,
				id: "step3",
				label: "Required different roles",
				Element: SelectRole,
				skipable: true,
				isValid: true,
			},
			{
				index: 3,
				id: "step4",
				label: "Allow dashboard",
				Element: Dashboard,
				skipable: true,
				isValid: true,
			},
			{
				index: 4,
				id: "step5",
				label: "Send request",
				Element: RequestOverview,
				skipable: false,
				isValid: true,
			},
		],
		[valid, value]
	);

	const isFinalStep = React.useMemo(
		() => activeStep === CREATE_NEW_APPLICATION_STEPS.length - 1,
		[CREATE_NEW_APPLICATION_STEPS.length, activeStep]
	);

	const onClickNext = React.useCallback(() => {
		if (!isFinalStep) {
			setActiveStep((prev) => prev + 1);
		} else onCreateApplication();
	}, [isFinalStep, onCreateApplication]);

	const onBack = React.useCallback(() => {
		if (activeStep !== 0) {
			setActiveStep((prev) => prev - 1);
		}
	}, [activeStep]);

	return (
		<Layout
			steps={CREATE_NEW_APPLICATION_STEPS}
			activeStep={activeStep}
			onClickNext={onClickNext}
			isFinalStep={isFinalStep}
			onBack={onBack}
			userLoading={userLoading}
			loadingCreateNewApp={loadingCreateNewApp}
			toast={toast}
			setToast={setToast}
			value={value}
			valid={valid}
			validation={validation}
			shouldValidate={shouldValidate}
			handleChange={handleChange}
		/>
	);
};

export default CreateNewApplicationForm;
